
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { SubscriptionManager } from "@ramudden/core/utils";
import { TaskApi } from "@ramudden/data-access/resource/task.api";
import { FilterDescriptor, FilterOperator, SearchParameters, ServiceRequestOptions } from "@ramudden/models/search";
import { ITask, TaskStatus } from "@ramudden/models/task";
import { SvgComponent } from "@ramudden/ui";
import moment from "moment/moment";
import { firstValueFrom } from "rxjs";
import { DefaultComponent } from "../../../layout/content/default/default.component";
import { AppService } from "../../../services/app.service";
import { AssignmentService } from "../../../services/assignment.service";
import { LoaderService } from "../../../services/loader.service";
import { TaskCardComponent } from "./task-card/task-card.component";

/** Server side filter (TaskService.ExcludeAssignedControlTasksFilter) that hides Control tasks belonging to a worker. */
const excludeAssignedControlTasksField = "ExcludeAssignedControlTasks";

@Component({
    selector: "app-assignment-tasks",
    standalone: true,
    imports: [DefaultComponent, SvgComponent, TaskCardComponent, TranslateModule],
    templateUrl: "./assignment-tasks.component.html",
    styleUrl: "./assignment-tasks.component.scss",
})
export class AssignmentTasksComponent implements OnInit, OnDestroy {
    private readonly appService = inject(AppService);
    private readonly assignmentService = inject(AssignmentService);
    private readonly loaderService = inject(LoaderService);
    private readonly router = inject(Router);
    private readonly subscriptionManager = new SubscriptionManager();
    private readonly taskApi = inject(TaskApi);
    protected tasks: ITask[] = [];
    isReadonlyView = false;

    async ngOnInit() {
        this.isReadonlyView = this.appService.isReadonlyView;
        this.tasks = await this.fetchTasks();
    }
    ngOnDestroy() {
        this.subscriptionManager.clear();
    }
    get siteActive() {
        const plannedAssignment = this.assignmentService.selectedPlannedAssignment;
        return (
            plannedAssignment.currentTeamStatus?.teamArrival !== undefined &&
            plannedAssignment.currentTeamStatus?.teamDeparture === undefined
        );
    }

    get editableAssignment() {
        return this.assignmentService.selectedPlannedAssignment.isPlannedToday;
    }

    get canCreateControlTask(): boolean {
        return this.isReadonlyView && (this.appService.coordinatorMode || this.appService.inCoordinatorView);
    }

    createTask() {
        this.router.navigate(["/assignment", this.assignmentService.selectedPlannedAssignment.id, "task", "new"]);
    }

    createControlTask() {
        this.appService.isCoordinatorControlTask = true;
        this.router.navigate(["/assignment", this.assignmentService.selectedPlannedAssignment.id, "task", "new"]);
    }

    async onRefreshTasks() {
        this.tasks = await this.fetchTasks();
    }

    /**
     * Workers who can not do control work only get Control tasks hidden when those belong to a worker. Control tasks
     * without a worker - created from SigncoSERV or by a coordinator in coordinator view - concern the whole team and
     * stay visible. A coordinator in readonly view always sees Control tasks, including the one he just created.
     */
    private get excludeAssignedControlTasks(): boolean {
        if (this.isReadonlyView) return false;
        return this.appService.selectedWorker?.canDoControl === false;
    }

    /**
     * Team members only see the tasks of their own team. A coordinator browsing another team must not be filtered on
     * his own team members, that would hide the work of the team he is looking at.
     */
    private getWorkerFilterValue(): string {
        if (this.appService.inCoordinatorView) {
            const viewedWorkerIds = (this.appService.viewingPlannedEvent?.plannedWorkers ?? []).map(
                (plannedWorker) => plannedWorker.workerId,
            );
            return viewedWorkerIds.join("|");
        }

        return (this.appService.selectedWorkers ?? []).map((worker) => worker.id).join("|");
    }

    //region Fetch Tasks
    private async fetchTasks(): Promise<ITask[]> {
        const assignmentId = this.assignmentService.selectedPlannedAssignment.assignmentId;
        if (assignmentId <= 0) return Promise.resolve([] as ITask[]);

        const serviceRequestOptions = new ServiceRequestOptions();
        serviceRequestOptions.includes.add("Task", "Location");
        serviceRequestOptions.includes.add("Task", "CurrentStatus");
        serviceRequestOptions.includes.add("Task", "Assignment");

        const filterValue = this.getWorkerFilterValue();
        const excludeAssignedControlTasks = this.excludeAssignedControlTasks;

        //region Active search parameters
        const activeSearchParameters = new SearchParameters();
        activeSearchParameters.filter = [
            new FilterDescriptor("Assignment", assignmentId, FilterOperator.equals),
            new FilterDescriptor("TaskStatusId", TaskStatus.InProgress, FilterOperator.equals),
        ];

        if (excludeAssignedControlTasks) {
            activeSearchParameters.filter.push(
                new FilterDescriptor(excludeAssignedControlTasksField, true, FilterOperator.equals),
            );
        }

        if (filterValue.length > 0) {
            activeSearchParameters.filter.push(new FilterDescriptor("Worker", filterValue, FilterOperator.in));
        }
        //endregion

        //region Scheduled search parameters
        const scheduledSearchParameters = new SearchParameters();
        scheduledSearchParameters.filter = [
            new FilterDescriptor("Assignment", assignmentId, FilterOperator.equals),
            new FilterDescriptor("TaskStatusId", TaskStatus.Finished, FilterOperator.notEquals),
            new FilterDescriptor("AvailableFrom", moment().format("YYYY/MM/DD"), FilterOperator.lessThanOrEqualTo),
        ];

        if (excludeAssignedControlTasks) {
            scheduledSearchParameters.filter.push(
                new FilterDescriptor(excludeAssignedControlTasksField, true, FilterOperator.equals),
            );
        }

        if (filterValue.length > 0) {
            scheduledSearchParameters.filter.push(new FilterDescriptor("WorkerTasks", filterValue, FilterOperator.in));
        }
        //endregion

        //region Finished search parameters
        const finishedSearchParameters = new SearchParameters();
        finishedSearchParameters.filter = [
            new FilterDescriptor("Assignment", assignmentId, FilterOperator.equals),
            new FilterDescriptor("TaskStatusId", TaskStatus.Finished, FilterOperator.equals),
            new FilterDescriptor(
                "End",
                moment().subtract(1, "days").format("YYYY/MM/DD"),
                FilterOperator.greaterThanOrEqualTo,
            ),
        ];

        if (excludeAssignedControlTasks) {
            finishedSearchParameters.filter.push(
                new FilterDescriptor(excludeAssignedControlTasksField, true, FilterOperator.equals),
            );
        }

        if (filterValue.length > 0) {
            finishedSearchParameters.filter.push(new FilterDescriptor("Worker", filterValue, FilterOperator.in));
        }
        //endregion

        this.loaderService.show();
        const activeTasks = (
            await firstValueFrom(this.taskApi.search$(activeSearchParameters, serviceRequestOptions)).finally(() =>
                this.loaderService.hide(),
            )
        ).data;

        this.loaderService.show();
        const scheduledTasks = (
            await firstValueFrom(this.taskApi.search$(scheduledSearchParameters, serviceRequestOptions)).finally(() =>
                this.loaderService.hide(),
            )
        ).data;

        this.loaderService.show();
        const finishedTasks = (
            await firstValueFrom(this.taskApi.search$(finishedSearchParameters, serviceRequestOptions)).finally(() =>
                this.loaderService.hide(),
            )
        ).data;

        return [...activeTasks, ...scheduledTasks, ...finishedTasks].sort((a, b) => {
            // Sort by name asc, then by start asc
            const nameComparison = a.name.localeCompare(b.name);
            if (nameComparison !== 0) {
                return nameComparison;
            }
            return moment(a.start).diff(moment(b.start));
        });
    }
    //endregion
}
